import { CircularProgress, Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"

import { Datasets } from "../../../../../constants/enums"
import {
    CORE_ADDITIONAL_COMMENTS_REG,
    CORE_CAPACITY_REG,
    CORE_CATEGORY_REG,
    CORE_HARDWARE_VERSION_REG,
    CORE_MANUFACTURER_REG,
    CORE_MODEL_REG,
    CORE_PHASES_REG,
    CORE_SOFTWARE_VERSION_REG,
    CORE_TYPE_REG,
    registerDeviceLabelMapping,
} from "../../../../../constants/ttr/registerDevice"
import { useFilterReferenceMappings } from "../../../../../hooks/useFilterReferenceMappings"
import useUploadedDeviceAttachments from "../../../../../services/middleware/hooks/devices/common/useUploadedDeviceAttachments"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import {
    getDisplayName,
    getFullyTestedLabelName,
    getPartiallyTestedLabelName,
    goToPage,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import FormSummary from "../../../../formSummary/formSummary"
import Confirmation from "../../../../popUps/registerDevice/confirmation"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import { TTRDeviceFormData } from "../../types/providerTypes"

interface IReviewAndSubmitProps {
    context: Context<IDeviceFormContextValue<TTRDeviceFormData>>
}

/** Allows a user to look over their previouslly inputted values in the
 * publish device form
 */
const ReviewAndSubmit = ({ context }: IReviewAndSubmitProps): ReactElement => {
    const {
        formData,
        currentIndex,
        setCurrentIndex,
        deviceId,
        deviceRef,
        onSubmit,
        submitLoading,
        submitSuccessful,
        canEditManufacturer,
        saveDraft,
        saveDraftLoading,
        errors: contextErrors,
        attachmentUUID,
    } = useContext(context)
    const {
        manufacturerMappings,
        deviceCategoryMappings,
        deviceTypeMappings,
        devicePhaseMappings,
    } = useFilterReferenceMappings()

    const {
        uploadedAttachments,
        error: listAttachmentsError,
        isLoading: isLoadingUploadedAttachments,
        isValidating: isValidatingUploadedAttachments,
        error: uploadedAttachmentsError,
    } = useUploadedDeviceAttachments(Datasets.TTR, deviceRef, attachmentUUID)

    const uploadedAttachmentsAreValid =
        !isLoadingUploadedAttachments &&
        !isValidatingUploadedAttachments &&
        !uploadedAttachmentsError &&
        !!uploadedAttachments

    const deviceRefLoaded = deviceId && deviceRef

    const onClickNext = (): Promise<void> => onSubmit()
    const onClickBack = (): void => setCurrentIndex(currentIndex - 1)

    const goToManufacturerPage = (): void =>
        goToPage(0, canEditManufacturer, setCurrentIndex)

    const goToCoreDeviceDetailsPage = (): void =>
        goToPage(1, canEditManufacturer, setCurrentIndex)

    const goToTypeTestDetailsPage = (): void =>
        goToPage(2, canEditManufacturer, setCurrentIndex)

    const goToAttachmentsPage = (): void =>
        goToPage(3, canEditManufacturer, setCurrentIndex)

    const showManufacturerEdit = canEditManufacturer && !submitLoading
    const showPageEdit = !submitLoading

    const typeTestFieldMappings: Record<
        string,
        string | number | boolean | undefined
    > = {}

    formData.typeTestDetails.forEach((details) => {
        if (details.fullyTested) {
            typeTestFieldMappings[
                getFullyTestedLabelName(details.displayName)
            ] = details.fullyTested
        }

        if (details.partiallyTested) {
            typeTestFieldMappings[
                getPartiallyTestedLabelName(details.displayName)
            ] = details.partiallyTested
        }
    })

    const attachmentMappings: Record<
        string,
        string | number | boolean | undefined
    > = {}

    if (uploadedAttachmentsAreValid) {
        uploadedAttachments.forEach((summary) => {
            attachmentMappings[summary.fileName] = summary.description
        })
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    if (listAttachmentsError) {
        return <ErrorDisplay error={listAttachmentsError} />
    }

    return (
        <>
            <Confirmation
                isOpen={submitSuccessful}
                deviceRef={deviceRef}
                dataset={Datasets.TTR}
            />
            <Stack spacing={GRID_SPACING} paddingTop={GRID_SPACING}>
                <FormHeader
                    header="Review and Submit"
                    subHeader="Please check through your details prior to submitting"
                    index={currentIndex}
                    headerTypography={{
                        color: "text.primary",
                    }}
                />
                <FormSummary
                    summaries={[
                        {
                            title: "Manufacturer",
                            mappings: {
                                [registerDeviceLabelMapping[
                                    CORE_MANUFACTURER_REG
                                ]]: getDisplayName(
                                    manufacturerMappings,
                                    formData.coreDetails.manufacturer
                                ),
                            },
                            onEdit: showManufacturerEdit
                                ? goToManufacturerPage
                                : undefined,
                            omit: !canEditManufacturer,
                        },
                        {
                            title: "Core Device Details",
                            mappings: {
                                [registerDeviceLabelMapping[CORE_MODEL_REG]]:
                                    formData.coreDetails.model,
                                [registerDeviceLabelMapping[CORE_CATEGORY_REG]]:
                                    getDisplayName(
                                        deviceCategoryMappings,
                                        formData.coreDetails.deviceCategory
                                    ),
                                [registerDeviceLabelMapping[CORE_TYPE_REG]]:
                                    getDisplayName(
                                        deviceTypeMappings,
                                        formData.coreDetails.deviceType
                                    ),
                                [registerDeviceLabelMapping[CORE_CAPACITY_REG]]:
                                    formData.coreDetails.registeredCapacity ??
                                    "Unknown Value",
                                [registerDeviceLabelMapping[CORE_PHASES_REG]]:
                                    getDisplayName(
                                        devicePhaseMappings,
                                        formData.coreDetails.numberOfPhases
                                    ),
                                [registerDeviceLabelMapping[
                                    CORE_SOFTWARE_VERSION_REG
                                ]]: formData.coreDetails.softwareVersion,
                                [registerDeviceLabelMapping[
                                    CORE_HARDWARE_VERSION_REG
                                ]]: formData.coreDetails.hardwareVersion,
                                [registerDeviceLabelMapping[
                                    CORE_ADDITIONAL_COMMENTS_REG
                                ]]: formData.coreDetails.additionalComments,
                            },
                            onEdit: showPageEdit
                                ? goToCoreDeviceDetailsPage
                                : undefined,
                        },
                        {
                            title: "Type Test Details",
                            mappings: typeTestFieldMappings,
                            onEdit: showPageEdit
                                ? goToTypeTestDetailsPage
                                : undefined,
                        },
                        {
                            title: `Files - ${
                                uploadedAttachmentsAreValid
                                    ? uploadedAttachments.length
                                    : "Loading"
                            }`,
                            mappings: attachmentMappings,
                            onEdit: showPageEdit
                                ? goToAttachmentsPage
                                : undefined,
                        },
                    ]}
                />
                <FormNavigation
                    onClickBack={onClickBack}
                    onClickNext={onClickNext}
                    onClickMiddle={() => saveDraft(formData)}
                    nextText="Submit"
                    backText="Back"
                    nextDisabled={
                        !deviceRefLoaded || submitLoading || saveDraftLoading
                    }
                    backDisabled={submitLoading}
                    nextIcon={
                        submitLoading ? (
                            <CircularProgress size={20} />
                        ) : undefined
                    }
                />
            </Stack>
        </>
    )
}

export default ReviewAndSubmit
